import { lazy } from 'react';
import { createBrowserRouter } from "react-router";
import { DashboardLayout } from "./components/DashboardLayout";
import { LazyRoute } from "./components/LazyRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";
import type { UserRole } from "./context/AuthContext";

// ═══════════════════════════════════════════════════════════════
// EAGER IMPORTS — Organizados por categoria via barrels
// ═══════════════════════════════════════════════════════════════

// ── Public (marketing) ──
import { LandingPage, InteractiveDemo, PrivacyPolicyPage, TermsOfUsePage, LGPDPage, CookiePolicyPage, SLAPage } from "./pages/public";

// ── Auth ──
import { Login, ForgotPassword } from "./pages/auth";

// ── Dashboards ──
import { Dashboard } from "./pages/dashboards";

// ── Common ──
import { NotFound } from "./pages/common";

// ═══════════════════════════════════════════════════════════════
// LAZY IMPORTS — Code splitting por categoria
// ═══════════════════════════════════════════════════════════════

// ── Audits & Quality ──
const Audits = lazy(() => import("./pages/Audits").then(m => ({ default: m.Audits })));
const AuditDetail = lazy(() => import("./pages/AuditDetail").then(m => ({ default: m.AuditDetail })));
const Questionnaires = lazy(() => import("./pages/Questionnaires").then(m => ({ default: m.Questionnaires })));
const AnswerQuestionnaire = lazy(() => import("./pages/AnswerQuestionnaire").then(m => ({ default: m.AnswerQuestionnaire })));
const UploadAudit = lazy(() => import("./pages/UploadAudit").then(m => ({ default: m.UploadAudit })));
const AuditGroupDetail = lazy(() => import("./pages/AuditGroupDetail").then(m => ({ default: m.AuditGroupDetail })));
const MonitorAuditWorkspace = lazy(() => import("./pages/MonitorAuditWorkspace").then(m => ({ default: m.MonitorAuditWorkspace })));
const CallsList = lazy(() => import("./pages/CallsList").then(m => ({ default: m.CallsList })));
const HumanReview = lazy(() => import("./pages/HumanReview").then(m => ({ default: m.HumanReview })));
const Connectors = lazy(() => import("./pages/Connectors").then(m => ({ default: m.Connectors })));

// ── Training Dashboards ──
const DashboardTreinamentoSupervisor = lazy(() => import("./pages/training/DashboardTreinamentoSupervisor").then(m => ({ default: m.DashboardTreinamentoSupervisor })));
const DashboardTreinamentoQualidade = lazy(() => import("./pages/training/DashboardTreinamentoQualidade").then(m => ({ default: m.DashboardTreinamentoQualidade })));
const DashboardTreinamentoAdmin = lazy(() => import("./pages/training/DashboardTreinamentoAdmin").then(m => ({ default: m.DashboardTreinamentoAdmin })));

// ── Training ──
const Training = lazy(() => import("./pages/training/Training").then(m => ({ default: m.Training })));
const TrainingSession = lazy(() => import("./pages/TrainingSession").then(m => ({ default: m.TrainingSession })));
const TrainingResult = lazy(() => import("./pages/TrainingResult").then(m => ({ default: m.TrainingResult })));
const OperatorTrainingHub = lazy(() => import("./pages/OperatorTrainingHub").then(m => ({ default: m.OperatorTrainingHub })));
const TrainingCalendar = lazy(() => import("./pages/training/TrainingCalendar").then(m => ({ default: m.TrainingCalendar })));
const ScriptLibrary = lazy(() => import("./pages/ScriptLibrary").then(m => ({ default: m.ScriptLibrary })));
const Feedback360 = lazy(() => import("./pages/Feedback360").then(m => ({ default: m.Feedback360 })));
const Scenarios = lazy(() => import("./pages/training/Scenarios").then(m => ({ default: m.Scenarios })));

// ── Performance ──
const Goals = lazy(() => import("./pages/Goals").then(m => ({ default: m.Goals })));
const Ranking = lazy(() => import("./pages/Ranking").then(m => ({ default: m.Ranking })));
const Leagues = lazy(() => import("./pages/Leagues"));
const Certificates = lazy(() => import("./pages/Certificates").then(m => ({ default: m.Certificates })));
const Operators = lazy(() => import("./pages/Operators").then(m => ({ default: m.Operators })));
const OperatorDetail = lazy(() => import("./pages/OperatorDetail").then(m => ({ default: m.OperatorDetail })));
const OperatorComparison = lazy(() => import("./pages/performance/OperatorComparison").then(m => ({ default: m.OperatorComparison })));
const EvolutionHistory = lazy(() => import("./pages/EvolutionHistory").then(m => ({ default: m.EvolutionHistory })));

// ── Analytics ──
const Analytics = lazy(() => import("./pages/Analytics").then(m => ({ default: m.Analytics })));
const Reports = lazy(() => import("./pages/Reports").then(m => ({ default: m.Reports })));

// ── Operator ──
const OperatorProfile = lazy(() => import("./pages/OperatorProfile").then(m => ({ default: m.OperatorProfile })));

// ── Dashboards (lazy) ──
const SupervisorDashboard = lazy(() => import("./pages/SupervisorDashboard").then(m => ({ default: m.SupervisorDashboard })));
const SectorDashboard = lazy(() => import("./pages/SectorDashboard").then(m => ({ default: m.SectorDashboard })));
const QualityDashboard = lazy(() => import("./pages/QualityDashboard").then(m => ({ default: m.QualityDashboard })));
const CustomizableDashboard = lazy(() => import("./pages/CustomizableDashboard"));

// ── Common (lazy) ──
const NotificationCenter = lazy(() => import("./pages/NotificationCenter").then(m => ({ default: m.NotificationCenter })));

// ── Admin & SuperAdmin ──
const AdminUsers = lazy(() => import("./pages/AdminUsers").then(m => ({ default: m.AdminUsers })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const TenantManagement = lazy(() => import("./pages/TenantManagement"));
const WhiteLabel = lazy(() => import("./pages/WhiteLabel"));
const E2ETests = lazy(() => import("./pages/E2ETests"));
const SuperAdminRepository = lazy(() => import("./pages/superadmin/SuperAdminRepository").then(m => ({ default: m.SuperAdminRepository })));

// ═══════════════════════════════════════════════════════════════
// ROLE GROUPS — 5 perfis: superadmin, admin, qualidade, supervisor, operador
// ═══════════════════════════════════════════════════════════════
const SUPER_ADMIN_ONLY: UserRole[] = ['superadmin'];
const ADMIN_PLUS: UserRole[] = ['superadmin', 'admin'];
const ALL_ROLES: UserRole[] = ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'];
const MANAGEMENT_ROLES: UserRole[] = ['superadmin', 'admin', 'qualidade', 'supervisor'];
const QUALITY_ROLES: UserRole[] = ['superadmin', 'admin', 'qualidade'];
const GAMIFICATION_ROLES: UserRole[] = ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'];
const PERFORMANCE_VIEW_ROLES: UserRole[] = ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'];

// ═══════════════════════════════════════════════════════════════
// ROUTER
// ═══════════════════════════════════════════════════════════════
export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      // ── Dashboard (roteamento interno por role — eager) ──
      { 
        index: true, 
        Component: Dashboard,
      },

      // ══ QUALIDADE ══
      {
        path: "qualidade",
        element: <LazyRoute component={QualityDashboard} allowedRoles={[...QUALITY_ROLES]} />,
      },
      {
        path: "qualidade/fila-auditorias",
        element: <LazyRoute component={MonitorAuditWorkspace} allowedRoles={[...QUALITY_ROLES, 'supervisor']} />,
      },
      {
        path: "upload-auditoria",
        element: <LazyRoute component={UploadAudit} allowedRoles={[...QUALITY_ROLES]} />,
      },
      {
        path: "revisao-humana",
        element: <LazyRoute component={HumanReview} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "connectors",
        element: <LazyRoute component={Connectors} allowedRoles={['superadmin', 'admin']} />,
      },
      {
        path: "chamadas",
        element: <LazyRoute component={CallsList} allowedRoles={[...QUALITY_ROLES, "supervisor"]} />,
      },

      // ══ AUDITORIAS & MONITORAMENTO ══
      { 
        path: "auditorias",
        element: <LazyRoute component={Audits} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "auditorias/grupos/:auditGroupId",
        element: <LazyRoute component={AuditGroupDetail} allowedRoles={[...QUALITY_ROLES, 'supervisor']} />,
      },
      { 
        path: "auditorias/:auditId",
        element: <LazyRoute component={AuditDetail} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "questionarios",
        element: <LazyRoute component={Questionnaires} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "questionarios/:questionnaireId/responder",
        element: <LazyRoute component={AnswerQuestionnaire} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },

      // ══ ANALYTICS & RELATÓRIOS ══
      {
        path: "analitica",
        element: <LazyRoute component={Analytics} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "relatorios",
        element: <LazyRoute component={Reports} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },

      // ══ TRAINING DASHBOARDS ══
      {
        path: "treinamento-supervisor",
        element: <LazyRoute component={DashboardTreinamentoSupervisor} allowedRoles={['supervisor', 'admin', 'superadmin']} />,
      },
      {
        path: "qualidade/treinamento",
        element: <LazyRoute component={DashboardTreinamentoQualidade} allowedRoles={[...QUALITY_ROLES]} />,
      },
      {
        path: "admin/treinamento",
        element: <LazyRoute component={DashboardTreinamentoAdmin} allowedRoles={[...ADMIN_PLUS, 'qualidade']} />,
      },

      // ══ TREINAMENTO ══
      {
        path: "treino",
        element: <LazyRoute component={Training} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "cenarios",
        element: <LazyRoute component={Scenarios} allowedRoles={QUALITY_ROLES} />,
      },
      { 
        path: "treino/:scenarioId",
        element: <LazyRoute component={TrainingSession} allowedRoles={[...ALL_ROLES]} />,
      },
      { 
        path: "treino-resultado/:sessionId",
        element: <LazyRoute component={TrainingResult} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "centro-treinamento",
        element: <LazyRoute component={OperatorTrainingHub} allowedRoles={['operador']} />,
      },
      { 
        path: "calendario-treinos",
        element: <LazyRoute component={TrainingCalendar} allowedRoles={[...ALL_ROLES]} />,
      },
      { 
        path: "biblioteca-scripts",
        element: <LazyRoute component={ScriptLibrary} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "feedback-360",
        element: <LazyRoute component={Feedback360} allowedRoles={[...GAMIFICATION_ROLES]} />,
      },

      // ══ DESEMPENHO ══
      { 
        path: "metas",
        element: <LazyRoute component={Goals} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "ranking",
        element: <LazyRoute component={Ranking} allowedRoles={[...PERFORMANCE_VIEW_ROLES]} />,
      },
      {
        path: "ligas",
        element: <LazyRoute component={Leagues} allowedRoles={[...PERFORMANCE_VIEW_ROLES]} />,
      },
      {
        path: "certificados",
        element: <LazyRoute component={Certificates} allowedRoles={[...PERFORMANCE_VIEW_ROLES]} />,
      },
      {
        path: "operadores",
        element: <LazyRoute component={Operators} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "operadores/:operatorId",
        element: <LazyRoute component={OperatorDetail} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },
      {
        path: "comparacao-operadores",
        element: <LazyRoute component={OperatorComparison} allowedRoles={[...MANAGEMENT_ROLES]} />,
      },

      // ══ GESTÃO ══
      {
        path: "dashboard-personalizado",
        element: <LazyRoute component={CustomizableDashboard} allowedRoles={['superadmin', 'admin', 'qualidade', 'supervisor']} />,
      },
      { 
        path: "notificacoes",
        element: <LazyRoute component={NotificationCenter} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "historico-evolucao",
        element: <LazyRoute component={EvolutionHistory} allowedRoles={[...ALL_ROLES]} />,
      },
      {
        path: "dashboard-supervisor",
        element: <LazyRoute component={SupervisorDashboard} allowedRoles={['supervisor', 'admin']} />,
      },
      {
        path: "dashboard-setores",
        element: <LazyRoute component={SectorDashboard} allowedRoles={['admin', 'qualidade', 'supervisor']} />,
      },

      // ══ OPERADOR ══
      {
        path: "meu-perfil",
        element: <LazyRoute component={OperatorProfile} allowedRoles={['operador']} />,
      },

      // ══ ADMIN ══
      {
        path: "admin/usuarios",
        element: <LazyRoute component={AdminUsers} allowedRoles={[...ADMIN_PLUS]} />,
      },

      // ══ SUPERADMIN ══
      {
        path: "superadmin/dashboard",
        element: <LazyRoute component={SuperAdminDashboard} allowedRoles={SUPER_ADMIN_ONLY} />,
      },
      {
        path: "superadmin/empresas",
        element: <LazyRoute component={TenantManagement} allowedRoles={SUPER_ADMIN_ONLY} />,
      },
      {
        path: "superadmin/white-label",
        element: <LazyRoute component={WhiteLabel} allowedRoles={SUPER_ADMIN_ONLY} />,
      },
      {
        path: "superadmin/testes-e2e",
        element: <LazyRoute component={E2ETests} allowedRoles={SUPER_ADMIN_ONLY} />,
      },
      {
        path: "superadmin/repositorio",
        element: <LazyRoute component={SuperAdminRepository} allowedRoles={SUPER_ADMIN_ONLY} />,
      },

      // ══ GERAL ══
      { 
        path: "configuracoes",
        element: <LazyRoute component={Settings} allowedRoles={[...ALL_ROLES]} />,
      },

      // ══ 404 ══
      {
        path: "*",
        Component: NotFound,
      },
    ],
  },

  // ══ ROTAS PÚBLICAS (fora do DashboardLayout) ══
  {
    path: "/login",
    Component: Login,
  },
  {
    path: "/esqueci-senha",
    Component: ForgotPassword,
  },
  {
    path: "/landing",
    Component: LandingPage,
  },
  {
    path: "/demo",
    Component: InteractiveDemo,
  },
  {
    path: "/privacidade",
    Component: PrivacyPolicyPage,
  },
  {
    path: "/termos",
    Component: TermsOfUsePage,
  },
  {
    path: "/lgpd",
    Component: LGPDPage,
  },
  {
    path: "/cookies",
    Component: CookiePolicyPage,
  },
  {
    path: "/sla",
    Component: SLAPage,
  },
  {
    path: "*",
    Component: NotFound,
  },
]);
